import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import { AuthLayoutComponent, FormCheckboxComponent, FormInputComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { Language, VerificationMethod } from "../../../shared/constants/enums";
import {
  LoginResponse,
  MainResponseModel,
  VerificationRequestEmail,
  VerificationRequestSms,
  VerificationRequestSubmit,
} from "../../../shared/models";
import { LoginService, UserProfileApiService } from "../../../shared/services";

@Component({
  selector: "adaa-login-verification",
  imports: [ReactiveFormsModule, TranslatePipe, FormInputComponent, FormCheckboxComponent, AuthLayoutComponent],
  templateUrl: "./login-verification.html",
  styleUrl: "./login-verification.scss",
})
export class LoginVerificationComponent implements OnInit {
  private _router = inject(Router);
  private _formBuilder = inject(FormBuilder);
  private _loginService = inject(LoginService);
  private _userProfileApiService = inject(UserProfileApiService);
  private _destroyRef = inject(DestroyRef);

  user: LoginResponse;
  verificationForm: FormGroup;
  verificationMethod = VerificationMethod;
  readonly selectedMethod = signal<VerificationMethod>(VerificationMethod.EMAIL);
  readonly lockCounter = signal(0);

  private readonly _lockDurationInSeconds = 60;
  private _lockIntervalId: ReturnType<typeof setInterval> | undefined;

  constructor() {
    //You need to call this in the constructor not in onInit before the navigation is finished
    this.user = this._router.currentNavigation()?.extras.state?.["user"];

    if (!AdaaHelper.isDefined(this.user)) {
      this._router.navigate(["/login"]);
    }

    this._destroyRef.onDestroy(() => this._clearLockInterval());
  }

  public ngOnInit(): void {
    this._prepareForm();
  }

  public selectMethod(value: VerificationMethod): void {
    this.selectedMethod.set(value);
    this.verificationForm.get("method")?.setValue(value);
  }

  public sendVerificationCode(): void {
    const method = this.verificationForm.get("method")!.value as VerificationMethod;

    switch (method) {
      case VerificationMethod.EMAIL:
        this._sendVerificationCodeByEmail();
        break;
      case VerificationMethod.SMS:
        this._sendVerificationCodeBySms();
        break;
      default:
        break;
    }
  }

  public submit(): void {
    if (!this.verificationForm.valid) {
      return;
    }

    const model: VerificationRequestSubmit = {
      token: this.verificationForm.get("verificationCode")?.value,
      doNotAskAgain: this.verificationForm.get("rememberMe")?.value,
      userId: this.user.id,
    };

    this._loginService.validateVerificationCode(model).subscribe((response: MainResponseModel) => {
      if (response.responseCode === "1") {
        this._saveLoginInfo();
        this._onSuccessfulVerification();
      }
    });
  }

  private _sendVerificationCodeByEmail(): void {
    const model: VerificationRequestEmail = {
      email: this.user.email,
      text: "code",
      userId: this.user.id,
      subject: "",
    };

    this._loginService
      .fetchVerificationCode(model, VerificationMethod.EMAIL)
      .subscribe((response: MainResponseModel) => {
        if (response.responseCode === "1") {
          this._lockVerificationButton();
        }
      });
  }

  private _sendVerificationCodeBySms(): void {
    const model: VerificationRequestSms = {
      language: AdaaHelper.getCurrentLang(),
      text: "code",
      userId: this.user.id,
    };

    this._loginService.fetchVerificationCode(model, VerificationMethod.SMS).subscribe((response: MainResponseModel) => {
      if (response.responseCode === "1") {
        this._lockVerificationButton();
      }
    });
  }

  /**
   * @description To lock the verification button from keep sending requests to
   * the API, the user will be able to resend the verification code in defined number.
   */
  private _lockVerificationButton(): void {
    this._clearLockInterval();
    this.lockCounter.set(this._lockDurationInSeconds);

    this._lockIntervalId = setInterval(() => {
      const remaining = this.lockCounter() - 1;
      this.lockCounter.set(remaining);

      if (remaining > 0) {
        return;
      }

      this._clearLockInterval();
    }, 1000);
  }

  private _clearLockInterval(): void {
    if (this._lockIntervalId === undefined) {
      return;
    }

    clearInterval(this._lockIntervalId);
    this._lockIntervalId = undefined;
  }

  /**
   * @description To prepare the verification form
   */
  private _prepareForm(): void {
    this.verificationForm = this._formBuilder.group({
      method: this.verificationMethod.EMAIL,
      verificationCode: [null, [Validators.required, Validators.maxLength(6), noWhitespaceValidator()]],
      rememberMe: true,
    });
  }

  public getEncodeMobileNumber(): string {
    let encodedMobilePhone = "";
    if (this.user?.mobilePhone) {
      encodedMobilePhone = AdaaHelper.clone(this.user.mobilePhone);
      const lastThreeDigits = encodedMobilePhone.slice(-3);
      const mobileNumberStars = encodedMobilePhone.replace(/\S/gi, "*");
      const substr = mobileNumberStars.length - 3;
      encodedMobilePhone =
        AdaaHelper.getCurrentLang() === Language.English
          ? mobileNumberStars.slice(0, substr) + lastThreeDigits
          : lastThreeDigits + mobileNumberStars.slice(0, substr);
    }
    return encodedMobilePhone;
  }

  /**
   * @description To set cookies and user information in the localStorage
   */
  private _saveLoginInfo(): void {
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.checkLogin, "1", "string");
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.sessionId, this.user.sessionId, "string");

    //User to get the current user info in the system
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.user, this.user, "json");
  }

  private _onSuccessfulVerification() {
    this._userProfileApiService.getSecurityQuestion().subscribe({
      next: (response) => {
        if (response.inError) return this._router.navigateByUrl("/user-profile");
        return this._loginService.doLogin(response.responseData.length > 0, this.user);
      },
    });
  }
}
